import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import PilotBanner from "@/modules/racket_lab_admin/PilotBanner";
import RLNavSidebar from "@/modules/racket_lab_admin/RLNavSidebar";
import EnvelopeStateBadge from "@/modules/racket_lab_admin/EnvelopeStateBadge";
import AuditTimeline from "@/modules/racket_lab_admin/AuditTimeline";
import JSONInspector from "@/modules/racket_lab_admin/JSONInspector";
import ConfirmActionDialog from "@/modules/racket_lab_admin/ConfirmActionDialog";
import DispatchStatusBanner from "@/modules/racket_lab_admin/DispatchStatusBanner";
import useAdminApi from "@/hooks/useAdminApi";
import useAdminMutation from "@/hooks/useAdminMutation";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

const Field = ({ label, value, testId, mono = false }) => (
  <div data-testid={testId} className="flex flex-col gap-0.5">
    <span className="text-[10px] uppercase tracking-wide text-slate-500">{label}</span>
    <span className={`text-sm text-slate-800 break-all ${mono ? "font-mono text-xs" : ""}`}>{value ?? "—"}</span>
  </div>
);

/**
 * RLAdminEnvelopeDetail — Sub-sprint C.
 *
 * Detail + transition action bar + preview + dispatch-mock (only when
 * status === "queued"). Hard-delete UI never exposed.
 */
const RLAdminEnvelopeDetail = () => {
  const t = useRacketLabAdminT();
  const navigate = useNavigate();
  const { id } = useParams();
  const [preview, setPreview] = useState(null);
  const { data, loading, error, reload } = useAdminApi(
    `/admin/notification-envelopes/${encodeURIComponent(id)}`,
    { skip: !id },
  );
  const { run, loading: mutating } = useAdminMutation();

  const transition = async (target_status, reason) => {
    await run("post", `/admin/notification-envelopes/${encodeURIComponent(id)}/transition`, { target_status, reason });
    reload();
  };

  const onPreview = async () => {
    const r = await run("post", `/admin/notification-envelopes/${encodeURIComponent(id)}/preview`, {});
    setPreview(r);
  };

  const onDispatchMock = async () => {
    await run("post", "/admin/notifications/_ops/dispatch-mock", { envelope_id: id });
    reload();
  };

  const status = data?.status;
  const canQueue = status === "draft";
  const canDispatch = status === "queued";
  const canCancel = status === "draft" || status === "queued" || status === "failed";

  return (
    <div data-testid="rl-admin-envelope-detail-page" className="flex flex-col gap-4">
      <PilotBanner />
      <RLNavSidebar />
      <DispatchStatusBanner />

      <button
        type="button"
        data-testid="rl-envelope-detail-back"
        onClick={() => navigate("/admin/racket-lab/notifications/envelopes")}
        className="inline-flex w-fit items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> {t("notifications.envelopes.back")}
      </button>

      <header>
        <h1 className="text-xl font-semibold text-slate-900">{t("notifications.envelopes.detail_title")}</h1>
        <p className="text-xs text-slate-500">{t("notifications.envelopes.detail.subtitle")}</p>
      </header>

      {loading && <div data-testid="rl-envelope-detail-loading" className="rounded-md border border-slate-200 bg-white p-4 text-sm text-slate-500">{t("notifications.envelopes.loading")}</div>}
      {!loading && error && (
        <div data-testid="rl-envelope-detail-error" className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          {error.status === 404
            ? t("notifications.envelopes.not_found", { id: id?.slice(0, 12) || "" })
            : `${t("notifications.envelopes.detail.error_prefix")} ${error.status || ""}: ${error.detail || t("notifications.envelopes.detail.error_unknown")}`}
        </div>
      )}

      {!loading && !error && data && (
        <>
          <section data-testid="rl-envelope-detail-card" className="grid grid-cols-1 gap-4 rounded-md border border-slate-200 bg-white p-4 md:grid-cols-2">
            <Field label={t("notifications.envelopes.detail.fields.id")} value={data.id} testId="rl-envelope-field-id" mono />
            <Field label={t("notifications.envelopes.detail.fields.status")} value={<EnvelopeStateBadge status={data.status} />} testId="rl-envelope-field-status" />
            <Field label={t("notifications.envelopes.detail.fields.template")} value={data.template_key} testId="rl-envelope-field-template" />
            <Field label={t("notifications.envelopes.detail.fields.channel")} value={data.channel} testId="rl-envelope-field-channel" />
            <Field label={t("notifications.envelopes.detail.fields.recipient")} value={data.recipient_user_id} testId="rl-envelope-field-recipient" mono />
            <Field label={t("notifications.envelopes.detail.fields.language")} value={data.language} testId="rl-envelope-field-language" />
            <Field label={t("notifications.envelopes.detail.fields.from_role")} value={data.from_role} testId="rl-envelope-field-from-role" />
            <Field label={t("notifications.envelopes.detail.fields.org")} value={data.organisation_id} testId="rl-envelope-field-org" mono />
            <Field label={t("notifications.envelopes.detail.fields.ticket")} value={data.ticket_id} testId="rl-envelope-field-ticket" mono />
            <Field label={t("notifications.envelopes.detail.fields.dry_run")} value={String(data.dry_run)} testId="rl-envelope-field-dry-run" />
            <Field label={t("notifications.envelopes.detail.fields.dispatch_ref")} value={data.delivery_reference} testId="rl-envelope-field-dispatch-ref" mono />
            <Field label={t("notifications.envelopes.detail.fields.dispatched_at")} value={data.dispatched_at} testId="rl-envelope-field-dispatched-at" />
            <Field label={t("notifications.envelopes.detail.fields.last_error")} value={data.last_error} testId="rl-envelope-field-last-error" />
          </section>

          <section data-testid="rl-envelope-actions" className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-white p-3">
            <ConfirmActionDialog
              dataTestId="rl-envelope-action-queue"
              trigger={<button data-testid="rl-envelope-action-queue-btn" disabled={!canQueue || mutating} className={`rounded-md px-3 py-1.5 text-xs font-medium text-white ${canQueue ? "bg-sky-600 hover:bg-sky-700" : "bg-slate-300 cursor-not-allowed"}`}>{t("notifications.envelopes.detail.actions.queue_btn")}</button>}
              title={t("notifications.envelopes.detail.actions.queue_title")}
              description={t("notifications.envelopes.detail.actions.queue_description")}
              confirmLabel={t("notifications.envelopes.detail.actions.queue_confirm")}
              onConfirm={() => transition("queued", "ui_transition")}
              disabled={!canQueue || mutating}
            />
            <ConfirmActionDialog
              dataTestId="rl-envelope-action-cancel"
              trigger={<button data-testid="rl-envelope-action-cancel-btn" disabled={!canCancel || mutating} className={`rounded-md px-3 py-1.5 text-xs font-medium text-white ${canCancel ? "bg-rose-600 hover:bg-rose-700" : "bg-slate-300 cursor-not-allowed"}`}>{t("notifications.envelopes.detail.actions.cancel_btn")}</button>}
              title={t("notifications.envelopes.detail.actions.cancel_title")}
              description={t("notifications.envelopes.detail.actions.cancel_description")}
              confirmLabel={t("notifications.envelopes.detail.actions.cancel_confirm")}
              onConfirm={() => transition("cancelled", "ui_cancel")}
              disabled={!canCancel || mutating}
              tone="danger"
            />
            <button
              type="button"
              data-testid="rl-envelope-preview-btn"
              onClick={onPreview}
              disabled={mutating}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium hover:bg-slate-50"
            >
              {t("notifications.envelopes.detail.actions.preview_btn")}
            </button>
            <ConfirmActionDialog
              dataTestId="rl-envelope-dispatch-mock"
              trigger={
                <button
                  data-testid="rl-envelope-dispatch-mock-btn"
                  disabled={!canDispatch || mutating}
                  className={`ml-auto rounded-md px-3 py-1.5 text-xs font-medium text-white ${canDispatch ? "bg-amber-600 hover:bg-amber-700" : "bg-slate-300 cursor-not-allowed"}`}
                >
                  {t("notifications.envelopes.detail.actions.dispatch_btn")}
                </button>
              }
              title={t("notifications.envelopes.detail.actions.dispatch_title")}
              description={t("notifications.envelopes.detail.actions.dispatch_description")}
              confirmLabel={t("notifications.envelopes.detail.actions.dispatch_confirm")}
              onConfirm={onDispatchMock}
              disabled={!canDispatch || mutating}
            />
          </section>

          {preview && (
            <section data-testid="rl-envelope-preview-result" className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
              <p className="mb-2 text-xs font-medium text-emerald-900">{t("notifications.envelopes.detail.preview_title")}</p>
              <JSONInspector value={preview} dataTestId="rl-envelope-preview-json" />
            </section>
          )}

          <section data-testid="rl-envelope-audit-section">
            <h2 className="mb-2 text-sm font-medium text-slate-700">{t("notifications.envelopes.detail.audit_timeline_title")}</h2>
            <AuditTimeline entries={data.audit || []} dataTestId="rl-envelope-audit-timeline" />
          </section>

          <section data-testid="rl-envelope-json-section">
            <h2 className="mb-2 text-sm font-medium text-slate-700">{t("notifications.envelopes.detail.raw_document_title")}</h2>
            <JSONInspector value={data} dataTestId="rl-envelope-json" />
          </section>
        </>
      )}
    </div>
  );
};

export default RLAdminEnvelopeDetail;
